import { usePasswordValidation } from '@lib/hooks/auth/password-criteria';
import { useTurnstile } from '@lib/hooks/auth/useTurnstile';
import { ApiError } from '@lib/network/api';
import { isEmailValid } from '@lib/utils/email';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { loginPreferences } from '@stores/LoginPreferences';
import { trackingStore } from '@stores/Tracking';
import { userStore } from '@stores/User';
import classNames from 'classnames';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Link, useSearchParams } from 'react-router-dom';
import { AuthButton } from '../AuthButton';
import { AuthErrorMessage } from '../AuthErrorMessage';
import { isRateLimitError, isTurnstileError } from '../authErrors';
import { AuthField } from '../AuthField';
import { BackToLogin } from '../BackToLogin';
import { PasswordValidationComponent } from '../PasswordValidation/PasswordValidationComponent';
import css from './RegisterForm.module.scss';

const TURNSTILE_CONTAINER_ID = 'turnstile-register';

// Link back to the magic-link sign-up form (/register), carrying any email the
// user already typed plus a post-login redirect so switching methods never
// loses their input.
function magicLinkRegisterHref(email: string, redirect: string | null): string {
  const search = new URLSearchParams();
  if (email) search.set('email', email);
  if (redirect) search.set('redirect', redirect);
  const qs = search.toString();
  return qs ? `/register?${qs}` : '/register';
}

export const RegisterForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect');
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const passwordValidation = usePasswordValidation(password, confirmPassword);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getToken, reset: resetTurnstile } = useTurnstile({ containerId: TURNSTILE_CONTAINER_ID });
  const isSignupDisabled = loading || !acceptedTerms;

  const submit = async () => {
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }
    if (!isEmailValid(email)) {
      setError('Invalid email');
      return;
    }
    if (password !== confirmPassword) {
      setError('Password fields do not match.');
      return;
    }
    if (!acceptedTerms) {
      setError('You must accept the Terms of Service and Privacy Policy');
      return;
    }

    if (passwordValidation.some((condition) => !condition.satisfied)) {
      setError(
        "For your account's security, please ensure your password meets all the listed criteria below.",
      );
      return;
    }

    setLoading(true);
    try {
      const turnstileToken = await getToken();
      const redirect = redirectParam || undefined;
      await userStore.register({ email, password, turnstileToken, redirect });
      // They created a password account — remember password as this device's
      // method so the next visit defaults to it.
      loginPreferences.setPreferredMethod('password');
      trackingStore.loginMethodSelected('password', 'register');
      navigate(`/verify-email?email=${encodeURIComponent(email)}`);
    } catch (e) {
      // The Turnstile token is single-use and was consumed by the register
      // request above. Reset the widget on every failure so a retry fetches a
      // fresh token — otherwise the next attempt reuses the spent token and
      // fails the (now genuine) Turnstile check. See useTurnstile.
      resetTurnstile();
      if (e instanceof ApiError) {
        trackingStore.signUpFailed(e.message);
        if (isTurnstileError(e)) {
          setError('Verification failed. Please complete the check again.');
        } else if (e.status === 409) {
          navigate(`/login?email=${encodeURIComponent(email)}&err=exists`);
        } else if (isRateLimitError(e)) {
          setError('Too many attempts. Please try again later.');
        } else if (e.status === 400 && e.message.includes('must be a valid email')) {
          setError('Invalid email');
        } else {
          setError(e.message);
        }
      } else {
        trackingStore.signUpFailed('An unexpected error occurred');
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    submit();
  };

  return (
    <Box className={css.loginFormBox}>
      <Box className={css.loginHeaderText}>
        <h1>Register</h1>
        <span>You're one step away from getting started.</span>
      </Box>
      <form className={css.form} onSubmit={handleSubmit}>
        <div>
          <AuthField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            error={!!error && error.toLowerCase().includes('email')}
            autoComplete="email"
            autoFocus
          />
          <AuthField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!error && error.toLowerCase().includes('password')}
            disabled={loading}
            required
            showPassword={showPassword}
            onTogglePassword={() => setShowPassword(!showPassword)}
            autoComplete="new-password"
          />
          <AuthField
            fullWidth
            label="Confirm password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={!!error && password !== confirmPassword}
            disabled={loading}
            showPassword={showPassword}
            onTogglePassword={() => setShowPassword(!showPassword)}
            autoComplete="new-password"
          />
          {error && <AuthErrorMessage message={error} />}
        </div>
        <PasswordValidationComponent passwordValidation={passwordValidation} />
        <div className={css.termsContainer}>
          <FormControlLabel
            control={
              <Checkbox
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                disabled={loading}
              />
            }
            disabled={loading}
            label={
              <div className={classNames(css.termsText, { [css.accepted]: acceptedTerms })}>
                I accept the{' '}
                <a
                  href="https://starzero.ai/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={css.termsLink}
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="https://starzero.ai/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={css.termsLink}
                >
                  Privacy Policy
                </a>
                .
              </div>
            }
            className={css.termsCheckbox}
          />
          <div id={TURNSTILE_CONTAINER_ID} />
          <AuthButton type="submit" disabled={isSignupDisabled} shineBehind={!isSignupDisabled}>
            {loading ? 'Signing you up...' : 'Sign up'}
          </AuthButton>
          <div className={css.secondaryToggle}>
            <Link className={css.toggleLink} to={magicLinkRegisterHref(email, redirectParam)}>
              Sign up with a sign-in link instead
            </Link>
          </div>
          <BackToLogin />
        </div>
      </form>
    </Box>
  );
};
